import { observable, configure, action, computed } from 'mobx';
import LocalModel from './localModel';

import util from 'preact-util';
import { route } from 'preact-router';
import PubSub, { topics } from '../lib/pubsub';

configure({ enforceActions: 'always' });

class ProductStore extends LocalModel {
    constructor() {
        super('product', {
            namePlural: 'products',
            sort: 'name',
            limit: 5000,
            api: {
                search: {
                    url: '/api/products/',
                    params: {
                        limit: 5000,
                        // sort: 'name',
                        sort: '-createdDate',
                    },
                },
                load: {
                    url: '/api/products/',
                    params: {},
                },
                save: {
                    url: '/api/products/',
                    params: {},
                },
                delete: {
                    url: '/api/products/',
                    params: {},
                },
            },
        });
    }

    @observable total = 1000;

    @observable limit = 100;

    @observable offset = 0;

    @observable currentPage = 1;

    @observable newProduct = {};

    @observable product = {};

    @observable products = [];

    @observable allProducts = [];

    @observable latestUpdateProducts = [];

    @observable searchResults = [];

    @observable productJournals = [];

    @observable productDrugs = [];

    @observable tags = [];

    @observable dealers = [];

    @observable purchaseDetails = [];

    @observable productStats = [];

    @observable productWeeklyStats = [];

    @observable productStatsSummary = {};

    @observable employeePickStats = [];

    @observable employeePickWeeklyStats = [];

    @observable stockPredictions = [];

    @observable tableWeeklyStats = [];

    @observable accountingPlan = {
        mainAccounts: [], genericAccounts: [], totalProducts: 0, productsWithSegments: 0,
    };

    @observable imports = [];

    async loadProductStats() {
        const response = await util.fetchApi('/api/products/stats', { method: 'GET' });
        if (response?.status === 200) {
            this.updateKeyValue('productStats', response.data?.productStats || []);
            this.updateKeyValue('productWeeklyStats', response.data?.weeklyStats || []);
            this.updateKeyValue('productStatsSummary', response.data?.summary || {});
            this.updateKeyValue('employeePickStats', response.data?.employeePickStats || []);
            this.updateKeyValue('employeePickWeeklyStats', response.data?.employeePickWeeklyStats || []);
            this.updateKeyValue('stockPredictions', response.data?.stockPredictions || []);
        }
        return response;
    }

    async loadAccountingPlan({ search, category, account, dealer } = {}) {
        const params = {};
        if (search) params.search = search;
        if (category) params.category = category;
        if (account) params.account = account;
        if (dealer) params.dealer = dealer;
        const response = await util.fetchApi('/api/products/accounting-plan', { method: 'GET' }, params);
        if (response?.status === 200) {
            this.updateKeyValue('accountingPlan', response.data || {
                mainAccounts: [], genericAccounts: [], totalProducts: 0, productsWithSegments: 0,
            });
        }
        return response;
    }

    async loadTableWeeklyStats(productIds) {
        if (!productIds || productIds.length === 0) {
            this.updateKeyValue('tableWeeklyStats', []);
            return;
        }
        const response = await util.fetchApi(`/api/products/weekly-stats?productIds=${productIds.join(',')}`, { method: 'GET' });
        if (response?.status === 200) {
            this.updateKeyValue('tableWeeklyStats', response.data?.weeklyStats || []);
        }
        return response;
    }

    lineTotal(line, format = true, inputQty) {
        const qty = inputQty || line.qty;
        if (!qty) return 0;

        let aPrice = line.price;
        if (line.medicine || line.fee || line.equipment || line.other) {
            aPrice = parseFloat(line.medicine || 0) + parseFloat(line.fee || 0) + parseFloat(line.equipment || 0) + parseFloat(line.other || 0);
        }
        let price = qty * (aPrice || 0);
        if (line.discount) {
            price -= price * (line.discount / 100);
        }
        const taxAmount = price * (line.vat / 100);
        let totalPrice = price + taxAmount;
        if (line.discountAmount) {
            totalPrice -= (line.discountAmount * qty);
        }
        if (format) {
            return util.format(totalPrice, 2, ',', ' ');
        }
        return totalPrice;
    }

    async updateAllPrices({ account, category, percent, search, dryRun = false, mode, amount, margin, components, rounding, roundIncVat }) {
        const body = {
            percent,
            account,
            category,
            search,
            dryRun,
        };
        if (mode) body.mode = mode;
        if (amount !== undefined && amount !== '') body.amount = amount;
        if (margin !== undefined && margin !== '') body.margin = margin;
        if (components) body.components = components;
        if (rounding !== undefined && rounding !== '') body.rounding = rounding;
        if (roundIncVat) body.roundIncVat = true;

        const response = await util.fetchApi(`/api/products/adjustallprices`, { method: 'PATCH' }, body);
        switch (response?.status) {
            case 200: // dry-run preview
            case 201: // actual update
                return response;
        }
    }

    async loadPurchaseDetails(id) {
        const response = await util.fetchApi(`/api/products/${id}/purchasedetails`);
        switch (response?.status) {
            case 200:
                this.updateKeyValue('purchaseDetails', response.data);
                this.updateKeyValue('product', response.included?.product);
                return response;
        }
    }

    async pickFromStock(id, qty) {
        const response = await util.fetchApi(`/api/products/${id}/pick`, { method: 'PATCH' }, {
            qty,
        });
        switch (response?.status) {
            case 201:
                // PubSub.publish(topics.NOTIFY, { message: 'product.picked', type: 'success', args: { qty, name: response.data.name } });
                this.updateObjectKeyValue('product', 'inStock', response.data?.product?.inStock);
                return response;
        }
        return response;
    }

    async findByBarcode(barcode) {
        if (!barcode) return null;
        const response = await util.fetchApi(`/api/products/?barcode=${encodeURIComponent(barcode)}`);
        switch (response?.status) {
            case 200:
                if (response.data && response.data.length > 0) {
                    return response.data[0];
                }
                return response.data;
        }
        return null;
    }

    async searchSpecies(search) {
        if (!search || search.length < 2) {
            return [];
        }
        const response = await util.fetchApi('/api/animalspecies/', { method: 'GET' }, {
            search,
            limit: 25,
        });
        if (response?.status === 200) {
            return response.data || [];
        }
        return [];
    }

    /**
     * Search products by free text (min 3 chars), optionally narrowed to one
     * product `category` (the select values in lib/fields.js). With a category
     * but no usable search text, the first 50 products of that category are
     * listed by name instead, so a picker can be browsed without typing.
     * @param {string} search
     * @param {{ category?: string }} [opts]
     */
    @action
    async searchProducts(search, { category } = {}) {
        const hasSearch = !!search && search.length >= 3;
        if (!hasSearch && !category) {
            return [];
        }
        const params = hasSearch
            ? { search, limit: 50 }
            : { limit: 50, sort: 'name' };
        if (category) {
            params.category = category;
        }
        const response = await util.fetchApi('/api/products/', { method: 'GET' }, params);
        if (response?.status === 200) {
            return response.data || [];
        }
        return [];
    }

    async saveTripletexProductId(productId, tripletexProductId) {
        const response = await util.fetchApi(`/api/products/${productId}`, {
            method: 'PATCH',
        }, { id: productId, tripletexProductId });
        if (response.status === 202) {
            // Update local product
            const product = this.products.find(p => p.id === productId);
            if (product) {
                product.tripletexProductId = tripletexProductId;
            }
        }
        return response;
    }

    matchProductsWithTripletexProducts(products, tripletexProducts, customerId) {
        // Match by product number pattern: {customerId}-{productNumber} or {customerId}-{productId}
        const matches = {};

        tripletexProducts.forEach(tp => {
            // Parse product number: "123-P001" -> { customerId: 123, productNumber: "P001" }
            // Also handles segment products: "123-P001-fee" -> base product is "123-P001"
            const match = tp.number?.match(/^(\d+)-(.+?)(?:-(?:fee|medicine|equipment|other))?$/);
            if (match && parseInt(match[1], 10) === customerId) {
                const localProductNumber = match[2];
                // Try to find by productNumber first, then by id
                let localProduct = products.find(p => p.productNumber === localProductNumber);
                if (!localProduct) {
                    localProduct = products.find(p => String(p.id) === localProductNumber);
                }
                if (localProduct) {
                    // If we already have a match for this product, use the base product (not segment)
                    if (!matches[localProduct.id]) {
                        matches[localProduct.id] = {
                            tripletexProduct: tp,
                            discrepancies: this.compareProductData(localProduct, tp),
                        };
                    }
                }
            }
        });
        return matches;
    }

    compareProductData(localProduct, tripletexProduct) {
        const discrepancies = [];
        // Compare VAT
        const localVat = localProduct.vat ?? 25;
        const tripletexVat = tripletexProduct.vatType?.percentage;
        if (tripletexVat !== undefined && localVat !== tripletexVat) {
            discrepancies.push({
                field: 'vat',
                fieldName: 'MVA',
                local: localVat,
                tripletex: tripletexVat,
            });
        }
        // Compare account
        const localAccount = localProduct.account || 3000;
        const tripletexAccount = tripletexProduct.account?.number;
        if (tripletexAccount && localAccount !== tripletexAccount) {
            discrepancies.push({
                field: 'account',
                fieldName: 'Konto',
                local: localAccount,
                tripletex: tripletexAccount,
            });
        }
        return discrepancies;
    }

    /**
     * Upload CSV price list for preview
     * @param {string} csvText - The CSV content
     * @param {Object} columnMapping - Optional column mapping override
     * @param {Object} vatSettings - VAT settings { priceIncludesVat, inPriceIncludesVat, defaultVatRate }
     * @returns {Promise<Object>} - Response with matched products and unmatched rows
     */
    async uploadPriceCsvPreview(csvText, columnMapping = {}, vatSettings = {}, matchMode = 'identifier') {
        const response = await util.fetchApi('/api/products/csv/price-upload', { method: 'POST' }, {
            csvText,
            columnMapping,
            vatSettings,
            matchMode,
        });
        return response;
    }

    /**
     * Apply price updates from CSV
     * @param {Array} updates - Array of { productId, newPrice, newInPrice }
     * @param {boolean} createNewProducts - Whether to create new products for unmatched rows
     * @param {Array} newProducts - Array of new products to create
     * @returns {Promise<Object>} - Response with update summary
     */
    async loadImports() {
        const response = await util.fetchApi('/api/products/imports', { method: 'GET' }, {});
        if (response && response.status === 200) {
            this.updateKeyValue('imports', response.data || []);
        }
        return response;
    }

    async removeImport(sourceList) {
        const response = await util.fetchApi(`/api/products/imports/${sourceList}/remove`, { method: 'POST' }, {});
        if (response && response.status === 200) { await this.loadImports(); }
        return response;
    }

    async restoreImport(sourceList) {
        const response = await util.fetchApi(`/api/products/imports/${sourceList}/restore`, { method: 'POST' }, {});
        if (response && response.status === 200) { await this.loadImports(); }
        return response;
    }

    async applyPriceUpdates(updates, createNewProducts = false, newProducts = [], sourceInfo = {}) {
        const body = { updates, createNewProducts, newProducts };
        if (sourceInfo && sourceInfo.sourceList) {
            body.sourceList = sourceInfo.sourceList;
            body.sourceListName = sourceInfo.sourceListName || '';
        }
        const response = await util.fetchApi('/api/products/csv/price-update', { method: 'PATCH' }, body);
        return response;
    }
}

const store = new ProductStore();
export default store;
